import {ParamsProps} from '@/components/WdiBus/FormPage';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {wdiCore, WdiModalAppResult} from '@/components/WdiControls';
import {FormSa} from '@/services/sales/sa';
import {FormApi} from '@/services/sales';
import {FormSs} from '@/services/sales/ss';
import {FormSp, FormSpDetail} from '@/services/sales/sp';
import DataStore from '@/components/GridWidget/DataStore';
import {EC} from '@/services/mdm/ec';
import {FileUploadUtility} from '@/components/WdiControls/WdiFileUpload';
import {DataOptions} from '@/services/basic/metadata';
import {WdiSelectFormat} from '@/components/WdiControls/WdiSelect';
import {Supplier} from '@/services/mdm/supplier';
import {Staff} from "@/services/basic/staff";
import {Invoices} from "@/services/sales/financial/invoices";

export type ViewEntityProps = {
  form?: API.Form;
  formSp?: API.FormSpVo;
  paymentMethod?: API.DataOptions;
  supplier?: API.Supplier;
  payToUser?: API.Staff;
  formSpDetails?: API.FormSpDetail[];
  relationType?: number;
  relatedFormId?: number;
  formId?: number;
  statusId?: number;
  formTypeId?: number;
  pageTypeId?: number;
  pageGroupId?: number;
  paymentMethodId?: number;
  payToTypeId?: number;
  companyCode?: string;
  supplierId?: number;
  payToUserId?: number;
  soldToCode?: string;
  vatTypeId?: number;
  reimburseType?: number;
  userId?: number;
  positionId?: number;
  deptId?: number;
  formSaId?: number;
  formSsId?: number;
  ecFieldConfigs?: API.EcFieldConfig[];
  allowViewSfa?: boolean;
  reimburseMaterials?: API.DataOptions[];
  soldToCodePayTo?: string;
};

export const initApplyPageContext = async (params: ParamsProps) => {
  const result: WdiModalAppResult = {
    code: ResponseStatus.UNKNOWN
  };
  const viewEntity: ViewEntityProps = {};
  if (params.formId || params.relatedFormId) {
    // 退回待修改
    const formResponse = await FormApi.get(params.formId || params.relatedFormId || 0);
    viewEntity.form = formResponse.data;
    if (!viewEntity.form) {
      result.message = "无效的退回待修改单";
      return result;
    }
    if (params.formId) {
      viewEntity.formId = params.formId;
    } else {
      viewEntity.relationType = Enum.FormRelationType.REJECTED;
      viewEntity.relatedFormId = params.relatedFormId;
    }
    const formSPResponse = await FormSp.get(viewEntity.form.formId);
    viewEntity.formSp = formSPResponse.data;
    viewEntity.reimburseType = viewEntity.formSp.reimburseType;
    viewEntity.userId = viewEntity.form.userId;
    viewEntity.positionId = viewEntity.form.positionId;
    viewEntity.deptId = viewEntity.form.deptId;
    viewEntity.pageTypeId = viewEntity.form.pageTypeId;
    viewEntity.pageGroupId = viewEntity.form.pageGroupId;
    viewEntity.formTypeId = viewEntity.form.formTypeId;
    viewEntity.supplierId = viewEntity.formSp.supplierId;
    viewEntity.payToTypeId = viewEntity.formSp.payToTypeId;
    viewEntity.payToUserId = viewEntity.formSp.payToUserId;
    viewEntity.companyCode = viewEntity.formSp.companyCode;
    const formSpDetailResp = await FormSpDetail.getFormSpDetails(viewEntity.form.formId);
    const firstFormSpDetail = formSpDetailResp.data[0];
    viewEntity.paymentMethodId = firstFormSpDetail.paymentMethodId;
    if (viewEntity.paymentMethodId !== Enum.PaymentMethod.THIRD_PART) {
      const soldToCodeSet = [...new Set(formSpDetailResp.data.map(c => c.soldToCode))];
      if (soldToCodeSet.length == 1) {
        viewEntity.soldToCode = soldToCodeSet[0];
      }
    }
    viewEntity.formSaId = firstFormSpDetail.formSaId;
    viewEntity.formSsId = firstFormSpDetail.formSsId;
    viewEntity.vatTypeId = firstFormSpDetail.vatTypeId;
  } else {
    // 直接申请
    viewEntity.paymentMethodId = params.paymentMethodId;
    viewEntity.companyCode = params.companyCode;
    viewEntity.supplierId = params.supplierId;
    viewEntity.payToTypeId = params.payToTypeId;
    viewEntity.payToUserId = params.payToUserId;
    viewEntity.soldToCode = params.soldToCode;
    viewEntity.pageTypeId = params.pageTypeId;
    viewEntity.pageGroupId = params.pageGroupId;
    viewEntity.formTypeId = params.formTypeId;
    viewEntity.formSaId = params.formSaId;
    viewEntity.reimburseType = params.reimburseType;
    viewEntity.userId = params.userId;
    viewEntity.positionId = params.positionId;
    viewEntity.deptId = params.deptId;
  }

  if (viewEntity.formSaId) {
    const formSaResponse = await FormSa.get(viewEntity.formSaId);
    if (!viewEntity.formSsId && formSaResponse?.data?.settlementMode != Enum.SettlementMode.NO_SETTLE) {
      const formSsResponse = await FormSs.getByFormSaId(viewEntity.formSaId);
      viewEntity.formSsId = formSsResponse?.data?.formSsId;
    }
  }

  const ecFieldConfigsResponse = await EC.getEcFieldConfigs();
  viewEntity.ecFieldConfigs = ecFieldConfigsResponse.data;

  if (viewEntity.pageGroupId == Enum.PageGroup.ACTIVITY_BATCH_PAYMENT) {
    if (viewEntity.formSp?.formSpId) {
      const formSPDetailsResponse = await FormSpDetail.getFormSpDetailsForSaBatchApply({
        formSpId: viewEntity.formSp?.formSpId,
      });
      viewEntity.formSpDetails = DataStore.init(formSPDetailsResponse.data);
    } else {
      viewEntity.formSpDetails = [];
    }
  } else {
    const formSPDetailsResponse = await FormSpDetail.getFormSpDetailsForSaApply({
      formSaId: viewEntity.formSaId,
      formSpId: viewEntity.formSp?.formSpId,
      soldToCode: viewEntity.soldToCode,
      paymentMethodId: viewEntity.paymentMethodId,
      companyCode: viewEntity.companyCode,
    });
    viewEntity.formSpDetails = DataStore.init(formSPDetailsResponse.data);
  }

  if (viewEntity.paymentMethodId) {
    const paymentMethodResponse = await DataOptions.search({
      typeGroup: 5,
      inValues: String(viewEntity.paymentMethodId)
    });
    viewEntity.paymentMethod = paymentMethodResponse.data[0];
  }

  if ((viewEntity.paymentMethodId == Enum.PaymentMethod.THIRD_PART)) {
    if (viewEntity.supplierId) {
      const supplierResponse = await Supplier.get(viewEntity.supplierId);
      viewEntity.supplier = supplierResponse.data;
    }
    if (viewEntity.payToUserId) {
      const userResp = await Staff.get(viewEntity.payToUserId);
      if (userResp.data)
        viewEntity.payToUser = userResp.data;
    }
  } else if (viewEntity.paymentMethodId == Enum.PaymentMethod.ACCOUNT_DEDUCT) {
    if (viewEntity.formSpDetails.length > 0) {
      viewEntity.soldToCodePayTo = viewEntity.formSpDetails[0].soldToCodePayTo;
    }
    if (wdiCore.Object.IsNullOrUndefined(viewEntity.soldToCodePayTo)) {
      viewEntity.soldToCodePayTo = viewEntity.soldToCode;
    }
  }
  const formSaIds = new Set(viewEntity.formSpDetails.map((item) => String(item.formSaId)));
  const soldToCodes = new Set(viewEntity.formSpDetails.map((item) => item.soldToCode));
  const reimburseMaterialsResponse = await DataOptions.getReimburseMaterials({
    formSaIds: Array.from(formSaIds).join(),
    paymentMethodIds: String(viewEntity.paymentMethodId),
    soldToCode: Array.from(soldToCodes).join(),
    companyCode: viewEntity.companyCode
  });
  viewEntity.reimburseMaterials = reimburseMaterialsResponse?.data;

  if (viewEntity.formSaId) {
    const allowViewSfaResponse = await FormSa.Buttons.allowViewSfaCollectionDetails(viewEntity.formSaId);
    viewEntity.allowViewSfa = allowViewSfaResponse.data;
  }

  result.code = ResponseStatus.SUCCESS;
  result.data = viewEntity;
  return result;
}

export const initApprovalPageContext = async (params: ParamsProps) => {
  const result: WdiModalAppResult = {
    code: ResponseStatus.UNKNOWN
  };
  const viewEntity: ViewEntityProps = {};
  if (params.formId) {
    // 从草稿打开
    const formSPResponse = await FormSp.getVo(params.formId);
    viewEntity.formSp = formSPResponse.data;
    if (!viewEntity.formSp) {
      result.message = "无效的单据";
      return result;
    }
    const formResponse = await FormApi.get(params.formId);
    viewEntity.form = formResponse.data;

    const formSPDetailsResponse = await FormSpDetail.getFormSpDetailsForSaApproval(params.formId);
    viewEntity.formSpDetails = DataStore.init(formSPDetailsResponse.data);
    viewEntity.vatTypeId = viewEntity.formSpDetails[0].vatTypeId;
    viewEntity.paymentMethodId = viewEntity.formSpDetails[0].paymentMethodId;
    if (viewEntity.form?.pageGroupId != Enum.PageGroup.ACTIVITY_BATCH_PAYMENT) {
      viewEntity.formSaId = viewEntity.formSpDetails[0].formSaId;
      viewEntity.formSsId = viewEntity.formSpDetails[0].formSsId;
    }

    const ecFieldConfigsResponse = await EC.getEcFieldConfigs();
    viewEntity.ecFieldConfigs = ecFieldConfigsResponse.data;

    const allowViewSfaResponse = await FormSp.Buttons.allowViewSfaCollectionDetails(params.formId);
    viewEntity.allowViewSfa = allowViewSfaResponse.data;

    //获取结案单ID（FormSP未存就拿FormSAID查）
    if (!viewEntity.formSsId) {
      const formSsResponse = await FormSs.getByFormSaId(viewEntity.formSaId || 0);
      viewEntity.formSsId = formSsResponse?.data?.formSsId;
    }
    const formSaIds = new Set(viewEntity.formSpDetails.map((item) => String(item.formSaId)));
    const soldToCodes = new Set(viewEntity.formSpDetails.map((item) => item.soldToCode));
    const reimburseMaterialsResponse = await DataOptions.getReimburseMaterials({
      formSaIds: Array.from(formSaIds).join(),
      paymentMethodIds: String(viewEntity.paymentMethodId),
      soldToCode: Array.from(soldToCodes).join(),
      companyCode: viewEntity.formSp.companyCode
    });
    viewEntity.reimburseMaterials = reimburseMaterialsResponse?.data;
  }

  result.code = ResponseStatus.SUCCESS;
  result.data = viewEntity;
  return result;
};

export const refreshApprovalPageContext = async (formId: number) => {
  const formSPResponse = await FormSp.getVo(formId);
  const formResponse = await FormApi.get(formId);
  // const formSPDetailsResponse = await FormSpDetail.getFormSpDetailsForSaApproval(formId);

  const data: { form: API.Form, formSp: API.FormSp, invoices?: API.FormInvoiceVo[] } = {
    form: formResponse.data,
    formSp: formSPResponse.data,
  };
  if (data.formSp?.invoiceStatus == Enum.InvoiceStatus.WITH_INVOICE) {
    const invoiceResp = await Invoices.getForPayment(formId);
    data.invoices = invoiceResp.data;
  }
  return data;
};

export const getPageResult = (values: any, viewEntity: ViewEntityProps, pageType: API.PageType, statusId: number,
                              dataSource: any[], invoiceDataSource?: any[], advanceDataSource?: any[], formGoods?: any[]) => {
  const result: WdiModalAppResult = {code: ResponseStatus.UNKNOWN};

  const {
    vatTypeId, payTypeId, bankAccountId, soldToCodePayTo,
    fileInfo, reimburseMaterials, oaFormNo, reversePrepayDetail, ...entity
  } = values;
  if (FileUploadUtility.isUploading(fileInfo)) {
    result.message = '附件正在上传中!';
    return result;
  }
  if (FileUploadUtility.isError(fileInfo)) {
    result.message = '附件存在错误文件!';
    return result;
  }
  const req: { form: any, formSp: any, formSpDetails: any[], invoices?: any[], formGoods?: any[] } = {
    form: {
      statusId: statusId,
      pageTypeId: pageType.pageTypeId,
      pageGroupId: pageType.pageGroupId,
      formTypeId: viewEntity.formTypeId,
      userId: viewEntity.userId,
      positionId: viewEntity.positionId,
      deptId: viewEntity.deptId
    },
    formSp: {},
    formSpDetails: [],
    invoices: [],
    formGoods: [],
  };

  if (viewEntity.formSp) {
    if (viewEntity.relationType === Enum.FormRelationType.REJECTED) {
      // 退回待修改
      req.form.relatedFormId = viewEntity.relatedFormId;
      req.form.relationType = viewEntity.relationType;
    } else {
      // 从草稿打开
      req.form.formId = viewEntity.formId;
    }
  }

  entity.modifiedReason = '新建';
  entity.formSaId = viewEntity.formSaId;
  entity.formSsId = viewEntity.formSsId;
  entity.reimburseType = viewEntity.reimburseType;

  const info = FileUploadUtility.serialize(fileInfo);
  entity.fileName = info?.names;
  entity.filePath = info?.urls;

  if (reimburseMaterials) {
    entity.reimburseMaterial = reimburseMaterials.join();
  }

  entity.vatTypeId = WdiSelectFormat.getvalue(vatTypeId);
  entity.payTypeId = WdiSelectFormat.getvalue(payTypeId);
  entity.bankAccountId = WdiSelectFormat.getvalue(bankAccountId);
  entity.soldToCodePayTo = WdiSelectFormat.getvalue(soldToCodePayTo);
  entity.paymentMethodId = viewEntity.paymentMethodId;
  entity.invoiceStatus = entity.invoiceStatus || Enum.InvoiceStatus.NON_INVOICE;
  entity.soldToCode = viewEntity.soldToCode;
  entity.supplierId = viewEntity.supplierId;
  entity.companyCode = viewEntity.companyCode;
  entity.payToTypeId = viewEntity.payToTypeId;
  entity.payToUserId = viewEntity.payToUserId;
  if (oaFormNo) {
    entity.oaFormNo = oaFormNo.oaFormNo;
    entity.oaFormName = oaFormNo.oaFormName;
    entity.url = oaFormNo.url;
  }
  if (reversePrepayDetail) {
    entity.reversePrepayDetail = values.reversePrepayDetail.map((item: any) => wdiCore.String.CodeOrName(item?.requisitionNumber, item?.transactionNum)).filter((item: any) => item).join()
  }

  req.formSp = entity;

  dataSource?.forEach((item, itemIndex) => {
    item.formSpDetailId = item.formSpDetailId || 0;
    item.formSpId = item.formSpId || 0;
  });
  req.formSpDetails = dataSource;
  req.invoices = invoiceDataSource;
  req.formGoods = formGoods;

  result.data = req;
  result.code = ResponseStatus.SUCCESS;
  return result;
}

export const moreConfig: any = {
  successParams: [
    {
      url: "/basicapi/favorite/getButtons",
      keys: []
    }, {
      url: "/expenseapi/spButtons/get",
      keys: [
        "UndoButton",
        "ReeditButton",
        "CancelButton",
        "InterveneFlowButton",
        "AppendExtNodeButton",
        "TransferNodeButton",
        "ViewSfaCollectionDetailsButton",
        "FormSpPrintButton",
        "AddMailApproverButton",
        "ExpediteFlowButton",
      ]
    }
  ],
  errorParams: [
    {
      url: "/expenseapi/spButtons/get",
      keys: [
        "RepairButton",
        "InterveneFlowButton"
      ]
    }
  ]
};

export const tpMoreConfig: any = {
  successParams: [
    {
      url: "/basicapi/favorite/getButtons",
      keys: []
    }, {
      url: "/expenseapi/spButtons/get",
      keys: [
        "UndoButton",
        "ReeditButton",
        "CancelButton",
        "ModifyPayOffDateButton",
        "InterveneFlowButton",
        "AppendExtNodeButton",
        "TransferNodeButton",
        "ViewSfaCollectionDetailsButton",
        "FormSpPrintButton",
        "ExpediteFlowButton",
      ]
    }
  ],
  errorParams: [
    {
      url: "/expenseapi/spButtons/get",
      keys: [
        "RepairButton",
        "InterveneFlowButton"
      ]
    }
  ]
};

export const fgMoreConfig: any = {
  successParams: [
    {
      url: "/basicapi/favorite/getButtons",
      keys: []
    }, {
      url: "/expenseapi/spButtons/get",
      keys: [
        "UndoButton",
        "ReeditButton",
        "CancelButton",
        "InterveneFlowButton",
        "AppendExtNodeButton",
        "TransferNodeButton",
        "ViewSfaCollectionDetailsButton",
        "FormSpPrintButton",
        "ExpediteFlowButton",
        "AddMailApproverButton",
      ]
    }
  ],
  errorParams: [
    {
      url: "/expenseapi/spButtons/get",
      keys: [
        "RepairButton",
        "InterveneFlowButton"
      ]
    }
  ]
};

export const approvalConfig: any = {
  params: {
    url: "/expenseapi/spButtons/get",
    keys: [
      "ApprovalButton",
      "ReadButton",
    ]
  }
};
